import {Directive, inject, viewChildren} from '@angular/core';
import {ToastService} from '../core/services/toast/toast.service';
import {StepComponent} from './step.component';
import {SubsidyType} from '../models/subsidy-type.type';
import {CompleteSubsidy} from '../models/complete-subsidy.model';
import {Attachment} from '../services/attachments/attachment.model';
import {finalize} from 'rxjs/operators';
import {LoadingService} from '../core/services/loading/loading.service';
import {AttachmentsService} from '../services/attachments/attachments.service';
import {AttachmentType} from '../services/attachments/attachment-type.enum';
import {
	AbstractControl,
	FormBuilder,
	FormControl,
	FormGroup,
	UntypedFormGroup,
	ValidationErrors,
	Validators
} from '@angular/forms';
import {Contact} from '../core/services/user/contact.model';
import {phoneValidator} from '../core/utils/phone-validator.util';
import {Address} from '../core/services/addresses/address.model';
import {SubTableComponent} from '../core/components/sub-table/sub-table.component';
import {atLeastOneCheckboxCheckedValidator} from '../core/utils/atleast-one-checkbox-validator';
import {getSubsidyConfig} from '../subsidies.config';
import {TableService} from '../services/subidies/table.service';

@Directive()
export abstract class StepWithAttachmentsComponent extends StepComponent<CompleteSubsidy> {
	tables = viewChildren(SubTableComponent);

	subsidyType: SubsidyType;
	STEPS: string[];

	readonly AttachmentType = AttachmentType;

	readonly attachmentsService = inject(AttachmentsService);
	readonly fb = inject(FormBuilder);
	readonly loadingService = inject(LoadingService);
	readonly tableService = inject(TableService);
	readonly toastService = inject(ToastService);

	constructor(subsidyType?: SubsidyType) {
		super(null as any);

		if (subsidyType) { // ELSE SET ALL MANUALLY!
			this.manuallySetSubsidyType(subsidyType);
		}
	}

	convertToCheckboxes(path: string, keys: any[], atleastOne: boolean = false): FormGroup {
		const group: any = {};

		keys.forEach(key => {
			group[key] = [!!(this.subsidy as any)[path].find((item: any) => item === key)];
		});

		if (atleastOne) {
			return this.fb.group(group, { validators: atLeastOneCheckboxCheckedValidator });
		}

		return this.fb.group(group);
	}

	createAddressGroup(address?: Address, withName: boolean = false): UntypedFormGroup {
		const group: any = {
			id: [address?.id],
			box: [address?.box],
			cityName: [address?.cityName, Validators.required],
			number: [address?.number, Validators.required],
			postalCode: [address?.postalCode, Validators.required],
			province: [address?.province],
			street: [address?.street, Validators.required],
		};

		if (withName) {
			group.name = [address?.name, Validators.required];
		}

		return this.fb.group(group);
	}

	createContactGroup(contact?: Contact | null, withAddress: boolean = false): UntypedFormGroup {
		const group: any = {
			email: [contact?.email || null, [Validators.required, Validators.email]],
			person: this.fb.group({
				firstName: [contact?.person?.firstName || null, Validators.required],
				functionName: [contact?.person?.functionName || null],
				lastName: [contact?.person?.lastName || null, Validators.required],
			}),
			telephoneNumber1: [contact?.telephoneNumber1 || null, [Validators.required, phoneValidator]],
			telephoneNumber2: [contact?.telephoneNumber2 || null, phoneValidator],
			telephoneNumber3: [contact?.telephoneNumber3 || null, phoneValidator],
		};

		if (withAddress) {
			group.address = this.createAddressGroup(contact?.address);
		}

		return this.fb.group(group);
	}

	deleteAttachment(attachment: Attachment) {
		this.loadingService.load('Bestand verwijderen...');

		this.attachmentsService
			.deleteAttachment(this.subsidyType, this.subsidy.id, attachment.id)
			.pipe(finalize(() => this.loadingService.done()))
			.subscribe(
				() => {
					this.subsidy.attachments = this.subsidy.attachments.filter((a) => a.id !== attachment.id);
				},
				(res) => this.toastService.serverError(res)
			);
	}

	getCheckbox(path: string, key: any): FormControl {
		return this.form.get(`${path}.${key}`) as FormControl;
	}

	hasMissingAttachmentOfType(type: AttachmentType, min: number = 1, withXls: boolean = false): boolean {
		const list = this.subsidy.attachments.filter((a) => a.type === type);

		if (withXls) {
			const hasXls = list.find((a) => a.filename.includes('.xls'));

			if (!hasXls) {
				return true;
			}
		}

		return list.length < min;
	}

	isChecked(path: string, key: any): boolean {
		const items = this.getCheckboxesData(path);
		return !!items.find(item => item === key);
	}

	uploadFile(data: { files: FileList; type: string }) {
		this.toastService.clearToast();

		for (let i = 0; i < data.files.length; i++) {
			const file = data.files.item(i);
			const error = this.attachmentsService.checkFileName(file!);

			if (error) {
				return this.toastService.error(error);
			}

			this.loadingService.load('Bestand uploaden...');

			this.attachmentsService
				.uploadAttachment(this.subsidyType, this.subsidy.id, file!, data.type)
				.pipe(finalize(() => this.loadingService.done()))
				.subscribe(
					(attachment) => {
						this.subsidy.attachments = [...this.subsidy.attachments, attachment];
					},
					(res) => this.toastService.serverError(res)
				);
		}
	}

	override isFormValid(valid: boolean = true, dontTriggerInvalidForm: boolean = false): boolean {
		const validTables = (this.tables() || []).filter(t => !t.isFormValid()).length === 0;
		return super.isFormValid(valid && validTables, dontTriggerInvalidForm);
	}

	protected atleastOneValidator(fn: (group: AbstractControl) => any[]): (group: AbstractControl) => ValidationErrors | null {
		return (group: AbstractControl): ValidationErrors | null => {
			const list = fn(group);

			if (list.length) {
				return null;
			}

			return {
				atleastOne: true,
			};
		};
	}

	protected listContains(list: any[] = [], value: any): boolean {
		return !!list.find(item => item === value);
	}

	protected getCheckboxes<T>(group: AbstractControl): T[] {
		const list: any[] = [];
		const values = group.value;

		for (const key in values) {
			if (values.hasOwnProperty(key)) {
				if (values[key]) {
					list.push(key);
				}
			}
		}

		return list;
	}

	protected getCheckboxesData(path: string): any[] {
		const group = this.form.get(path) as AbstractControl;

		const list = [];
		const values = group.value;

		for (const key in values) {
			if (values.hasOwnProperty(key)) {
				if (values[key]) {
					list.push(key as any);
				}
			}
		}

		return list;
	}

	protected manuallySetSubsidyType(type: SubsidyType) {
		this.subsidyType = type;

		const config = getSubsidyConfig(type);
		if (config) {
			this.STEPS = config.steps;
		}
	}

	protected override getData(formData: any): any {
		const tablesObject: any = {};

		(this.tables() || []).forEach(t => {
			if (!tablesObject[t.key()]) {
				tablesObject[t.key()] = [];
			}

			tablesObject[t.key()].push(...t.getData());
		});

		return super.getData({
			...formData,
			...tablesObject,
		});
	}
}
